import React from 'react';
import { Form, Formik } from 'formik';
import * as Yup from 'yup';

import { getErrorMessage } from '~libs/axiosWrapper';
import { useToast } from '~libs/toast';

import { Button, ErrorResponse, Modal } from '~components';
import { FieldProps, Fields } from '~components/form-fields';

import { CarePlanGoal } from '~clients/models';

import { useFieldValidationHighlights } from '~clients/hooks/useFieldValidationHighlights';

import {
  useCreateCarePlanGoalMutation,
  useGetCarePlanNeedsQuery,
  useGetGoalApproachTypesQuery,
  useGetGoalStatusesQuery,
  useUpdateCarePlanGoalMutation,
} from '~clients/actions/carePlans';

interface ComponentProps {
  planId: string;
  /** Present → edit mode; absent → create mode. */
  goal?: CarePlanGoal;
  /** Whether the plan type has assessed needs; false hides the "Linked need" selector. */
  hasNeeds: boolean;
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
}

interface GoalValues {
  [key: string]: string | number | boolean | object | null;
  description: string;
  need_id: string;
  approach_type_id: string;
  approach_description: string;
  outcome: string;
  status_id: string;
}

const GOAL_PLACEHOLDER = 'e.g. I want to be able to prepare my own meals again';

const validationSchema = Yup.object().shape({
  description: Yup.string().required('Goal is required'),
});

const GoalFormModal: React.FC<ComponentProps> = ({ planId, goal, hasNeeds, isOpen, setIsOpen }) => {
  const { showToast } = useToast();
  const [createGoal, { isLoading: isCreating, error: createError, reset: resetCreate }] =
    useCreateCarePlanGoalMutation();
  const [updateGoal, { isLoading: isUpdating, error: updateError, reset: resetUpdate }] =
    useUpdateCarePlanGoalMutation();

  const { data: needs } = useGetCarePlanNeedsQuery({ planId }, { skip: !hasNeeds });
  const { data: approachTypes } = useGetGoalApproachTypesQuery();
  const { data: statuses } = useGetGoalStatusesQuery();

  const { invalidFields, validate } = useFieldValidationHighlights(validationSchema);

  const isEdit = goal !== undefined;
  const isSaving = isCreating || isUpdating;
  // Error from the mutation relevant to this mode (avoids a stale error from the other).
  const error = isEdit ? updateError : createError;

  // Instance is reused across close/reopen for the same goal, so a failed attempt's error
  // would otherwise resurface on the next open. Clear both on close.
  React.useEffect(() => {
    if (!isOpen) {
      resetCreate();
      resetUpdate();
    }
  }, [isOpen, resetCreate, resetUpdate]);

  const needOptions = (needs ?? []).map((need) => ({ id: need.id, name: need.title || need.description }));
  const approachOptions = (approachTypes ?? []).map((type) => ({ id: type.id, name: type.name }));
  const statusOptions = (statuses ?? []).map((status) => ({ id: status.id, name: status.name }));

  // Built from live lookup/needs data, so defined in-render.
  const goalFields: FieldProps = {
    description: {
      type: 'textarea',
      caption: 'Goal',
      required: true,
      placeholder: GOAL_PLACEHOLDER,
      dataCy: 'care-plan-goal-description',
    },
    // Restorative Care plans have no assessed needs, so the linked-need selector is omitted.
    ...(hasNeeds
      ? {
          need_id: {
            type: 'select' as const,
            caption: 'Linked need',
            options: needOptions,
            placeholder: '-- No linked need --',
            dataCy: 'care-plan-goal-need',
          },
        }
      : {}),
    approach_type_id: {
      type: 'select',
      caption: 'Approach type',
      options: approachOptions,
      placeholder: '-- Select an approach type --',
      dataCy: 'care-plan-goal-approach-type',
    },
    approach_description: {
      type: 'textarea',
      caption: 'Approach description',
      dataCy: 'care-plan-goal-approach-description',
    },
    outcome: { type: 'textarea', caption: 'Measurable outcome', dataCy: 'care-plan-goal-outcome' },
    status_id: {
      type: 'select',
      caption: 'Status',
      options: statusOptions,
      placeholder: '-- Select a status --',
      dataCy: 'care-plan-goal-status',
    },
  };
  const definitions = Object.entries(goalFields).map(([key, props]) => ({ key, ...props }));

  const initialValues: GoalValues = {
    description: goal?.description ?? '',
    need_id: goal?.need_id ?? '',
    approach_type_id: goal?.approach_type_id ?? '',
    approach_description: goal?.approach_description ?? '',
    outcome: goal?.outcome ?? '',
    status_id: goal?.status_id ?? '',
  };

  const onSubmit = async (values: GoalValues) => {
    const body = {
      description: values.description,
      need_id: values.need_id || null,
      approach_type_id: values.approach_type_id || null,
      approach_description: values.approach_description || null,
      outcome: values.outcome || null,
      status_id: values.status_id || null,
    };
    try {
      if (isEdit) {
        await updateGoal({ id: goal.id, ...body }).unwrap();
      } else {
        await createGoal({ planId, ...body }).unwrap();
      }
      showToast('success', `Goal ${isEdit ? 'updated' : 'added'}`);
      setIsOpen(false);
    } catch {
      // Surfaced through the mutation error below.
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      setIsOpen={setIsOpen}
      header={isEdit ? 'Edit Goal' : 'Add Goal'}
      includeFooter={false}
      bodyClassName="care-plan-form-body"
      size="xl"
    >
      {error !== undefined && <ErrorResponse dataCy="care-plan-goal-form-error" message={getErrorMessage(error)} />}
      <Formik
        enableReinitialize
        initialValues={initialValues}
        validationSchema={validationSchema}
        validate={validate}
        validateOnMount={false}
        validateOnChange={invalidFields.length > 0}
        validateOnBlur={false}
        onSubmit={onSubmit}
      >
        {({ values, setFieldValue, setFieldTouched, touched, dirty }) => (
          <Form>
            <Fields
              definitions={definitions}
              values={values}
              setFieldValue={setFieldValue}
              setFieldTouched={setFieldTouched}
              touched={touched}
              invalidFields={invalidFields}
            />
            <div className="care-plan-goal-form-actions">
              <Button size="sm" color="warning" onClick={() => setIsOpen(false)}>
                Cancel
              </Button>
              <Button
                type="submit"
                size="sm"
                color="success"
                disabled={!dirty}
                loading={isSaving}
                dataCy="care-plan-goal-save"
              >
                {isEdit ? 'Save' : 'Add Goal'}
              </Button>
            </div>
          </Form>
        )}
      </Formik>
    </Modal>
  );
};

export default GoalFormModal;
